/**
 * Pattern Detector for Smart Template Selection
 *
 * Analyzes slide content to determine the best layout template.
 * Inspired by Gamma's content-aware component mapping.
 */

/**
 * Detect the best layout pattern for a slide based on its content.
 *
 * @param {Object} slide - Slide data { title, content, bulletPoints }
 * @param {number} index - Slide index (0-based)
 * @param {number} totalSlides - Total number of slides
 * @returns {string} Layout pattern name
 */
export function detectLayoutPattern(slide, index, totalSlides) {
  const title = slide.title || '';
  const content = slide.content || '';
  const bullets = slide.bulletPoints || slide.bullet_points || [];
  const combined = `${title} ${content}`.toLowerCase();
  const bulletsText = bullets.join(' ').toLowerCase();

  // Title slide - first slide
  if (index === 0) {
    return 'title';
  }

  // Big statement - medium-length title, no bullets, minimal content (impact slide)
  // Check before section so impactful single-line slides get the big-statement treatment
  if (title.length >= 20 && title.length <= 120 && bullets.length === 0 && content.length < 50 && index > 0) {
    return 'big-statement';
  }

  // Section divider - short title, no content, no bullets
  if (title.length > 0 && title.length < 60 && !content.trim() && bullets.length === 0) {
    return 'section';
  }

  // Timeline: years/dates pattern with multiple items
  const yearPattern = /\b(19|20)\d{2}\b/g;
  const broadDatePattern = /\b\d+\s*(?:BC|BCE|AD|CE)\b|\b\d+(?:st|nd|rd|th)\s+century\b|c\.\s*\d+/gi;
  const allText = combined + ' ' + bulletsText;
  const modernYears = allText.match(yearPattern);
  const broadDates = allText.match(broadDatePattern);
  const totalDates = (modernYears?.length || 0) + (broadDates?.length || 0);
  if (totalDates >= 2 && bullets.length >= 2) {
    return 'timeline';
  }

  // Funnel: marketing/sales funnel, pipeline, conversion stages
  const funnelPattern = /\bfunnel\b|pipeline|conversion\s*(stage|rate)|awareness.*consideration|top\s*of\s*funnel|bottom\s*of\s*funnel|tofu|mofu|bofu/i;
  if (funnelPattern.test(combined) && bullets.length >= 3) {
    return 'funnel';
  }

  // Pyramid: hierarchy, levels, tiers
  const pyramidPattern = /\bpyramid\b|hierarchy|tier\s*\d|maslow|layers?\s*of/i;
  if (pyramidPattern.test(combined) && bullets.length >= 3) {
    return 'pyramid';
  }

  // Cycle: recurring process, circular flow
  const cyclePattern = /\bcycle\b|circular|recurring|continuous|loop|pdca|plan.*do.*check.*act|iterative/i;
  if (cyclePattern.test(combined) && bullets.length >= 3) {
    return 'cycle';
  }

  // Venn: overlap, intersection, shared, common ground
  const vennPattern = /\bvenn\b|overlap|intersection|shared|common\s*ground|synergy|converge/i;
  if (vennPattern.test(combined) && bullets.length >= 2 && bullets.length <= 4) {
    return 'venn';
  }

  // Process/steps: step/phase/stage keywords with numbered items
  const stepPattern = /\b(step|phase|stage)\s*\d/i;
  const numberedBullets = bullets.filter(b => /^\s*\d+[\.\)\-]/.test(b));
  if (stepPattern.test(combined) && numberedBullets.length >= 2) {
    return 'process';
  }

  // Numbered list: numbered bullets without step/phase keywords (clean numbered list)
  if (numberedBullets.length >= 3 && !stepPattern.test(combined)) {
    return 'numbered-list';
  }

  // Versus: explicit vs comparison (specific two-sided)
  const versusPattern = /\bvs\.?\b|versus/i;
  if (versusPattern.test(combined) && bullets.length >= 2) {
    return 'versus';
  }

  // Comparison: compared to, before/after, pros/cons
  const comparisonPattern = /\bcompared?\s+to\b|before\s*(and|&|\/)\s*after|pros\s*(and|&|\/)\s*cons/i;
  if (comparisonPattern.test(combined) || comparisonPattern.test(bulletsText)) {
    return 'comparison';
  }

  // Metrics dashboard: multiple stats across bullets (3+ stat-like values)
  const statInBullets = bullets.filter(b => /\d{1,3}(?:,\d{3})*(?:\.\d+)?[%$MBKxX]/.test(b));
  if (statInBullets.length >= 3) {
    return 'metrics-dashboard';
  }

  // Stat highlight: prominent numbers with minimal bullets
  const statPatterns = [
    /\d{1,3}(?:,\d{3})*(?:\.\d+)?%/,  // Percentages: 45%, 12.5%
    /\$\d{1,3}(?:,\d{3})*(?:\.\d+)?[MBKmk]?/,  // Money: $1.5M, $500K
    /\d{2,}(?:\.\d+)?[xX](?!\w)/,  // Multipliers: 10x, 2.5X
    /\b\d{3,}(?:,\d{3})*\+?\b/,  // Large numbers: 1,000, 500+
  ];

  const hasProminentStat = statPatterns.some(p => p.test(combined));
  if (hasProminentStat && bullets.length <= 2 && content.length < 200) {
    return 'stat-highlight';
  }

  // Quote: quotation marks or attribution dash
  const quotePatterns = [
    /"[^"]{20,}"/,  // Straight quotes
    /"[^"]{20,}"/,  // Smart quotes
    /—\s*[A-Z][a-z]+/,  // Em dash attribution
    /–\s*[A-Z][a-z]+/,  // En dash attribution
    /\s-\s*[A-Z][a-z]+\s*$/,  // Hyphen attribution at end
  ];
  if (quotePatterns.some(p => p.test(combined))) {
    return 'quote';
  }

  // Feature showcase: 3-6 bullets with feature/benefit keywords
  const featurePattern = /\bfeature|benefit|advantage|what we offer|capabilit|service|highlight|our solution|why choose/i;
  if (featurePattern.test(combined) && bullets.length >= 3 && bullets.length <= 6) {
    return 'feature-showcase';
  }

  // Bullets: check count for single vs two-column
  // Alternate between layout styles based on slide index for visual variety
  if (bullets.length >= 7) {
    return 'bullets-two-col';
  }
  if (bullets.length >= 4) {
    const cardLayouts = ['cards', 'icon-grid', 'feature-showcase'];
    return cardLayouts[index % cardLayouts.length];
  }
  if (bullets.length >= 2) {
    return 'icon-bullets';
  }

  // Default: content (paragraph-focused)
  return 'content';
}

/**
 * Get layout metadata for a pattern
 *
 * @param {string} pattern - Layout pattern name
 * @returns {Object} Pattern metadata
 */
export function getPatternMetadata(pattern) {
  const metadata = {
    'title': {
      name: 'Title Slide',
      description: 'Opening slide with centered title and subtitle',
      supportsSubtitle: true,
      supportsBullets: false,
    },
    'section': {
      name: 'Section Divider',
      description: 'Clean break between major sections',
      supportsSubtitle: true,
      supportsBullets: false,
    },
    'bullets': {
      name: 'Bullet Points',
      description: 'Standard bullet list layout',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 4,
    },
    'bullets-two-col': {
      name: 'Two-Column Bullets',
      description: 'Bullets split across two columns for longer lists',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 8,
    },
    'stat-highlight': {
      name: 'Stat Highlight',
      description: 'Large number callout with supporting text',
      supportsSubtitle: true,
      supportsBullets: true,
      maxBullets: 2,
    },
    'timeline': {
      name: 'Timeline',
      description: 'Horizontal timeline for chronological items',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 5,
    },
    'process': {
      name: 'Process Flow',
      description: 'Step-by-step flow with arrows',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 5,
    },
    'comparison': {
      name: 'Comparison',
      description: 'Two-column layout for comparing items',
      supportsSubtitle: false,
      supportsBullets: true,
    },
    'quote': {
      name: 'Quote',
      description: 'Large quote with attribution',
      supportsSubtitle: true,
      supportsBullets: false,
    },
    'content': {
      name: 'Content',
      description: 'Paragraph-focused text layout',
      supportsSubtitle: true,
      supportsBullets: true,
      maxBullets: 3,
    },
    'icon-bullets': {
      name: 'Icon Bullets',
      description: 'Bullet points with auto-selected icons',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 6,
    },
    'icon-grid': {
      name: 'Icon Grid',
      description: 'Grid of icons with labels',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 9,
    },
    'cards': {
      name: 'Cards',
      description: 'Content in styled card boxes',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 6,
    },
    'funnel': {
      name: 'Funnel',
      description: 'Funnel diagram for stages/pipeline',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 6,
    },
    'pyramid': {
      name: 'Pyramid',
      description: 'Layered pyramid for hierarchy',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 6,
    },
    'venn': {
      name: 'Venn Diagram',
      description: 'Overlapping circles for relationships',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 4,
    },
    'quadrant': {
      name: 'Quadrant Matrix',
      description: '2x2 matrix for categorization',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 4,
    },
    'bullseye': {
      name: 'Bullseye',
      description: 'Concentric target for priorities',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 5,
    },
    'cycle': {
      name: 'Cycle',
      description: 'Circular diagram for recurring processes',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 6,
    },
    'staircase': {
      name: 'Staircase',
      description: 'Ascending steps for progression',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 6,
    },
    'chain': {
      name: 'Chain',
      description: 'Connected nodes for linked concepts',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 6,
    },
    'versus': {
      name: 'Versus',
      description: 'Two-panel comparison with VS',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 2,
    },
    'iceberg': {
      name: 'Iceberg',
      description: 'Above/below water visualization',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 6,
    },
    'feature-showcase': {
      name: 'Feature Showcase',
      description: 'Grid of feature cards with icons',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 6,
    },
    'numbered-list': {
      name: 'Numbered List',
      description: 'Prominent numbered items with descriptions',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 8,
    },
    'big-statement': {
      name: 'Big Statement',
      description: 'One bold statement filling the slide',
      supportsSubtitle: false,
      supportsBullets: false,
    },
    'metrics-dashboard': {
      name: 'Metrics Dashboard',
      description: 'KPI stat boxes in a row',
      supportsSubtitle: false,
      supportsBullets: true,
      maxBullets: 4,
    },
  };

  return metadata[pattern] || metadata['content'];
}

/**
 * Analyze all slides and return pattern assignments
 *
 * @param {Array} slides - Array of slide objects
 * @returns {Array} Array of { index, pattern, metadata }
 */
export function analyzeSlidePatterns(slides) {
  return slides.map((slide, index) => {
    const pattern = detectLayoutPattern(slide, index, slides.length);
    return {
      index,
      pattern,
      metadata: getPatternMetadata(pattern),
    };
  });
}

/**
 * Extract statistics from slide content for stat-highlight template
 *
 * @param {Object} slide - Slide data
 * @returns {Array} Array of { value, label } objects
 */
export function extractStatistics(slide) {
  const combined = `${slide.title || ''} ${slide.content || ''}`;
  const stats = [];

  // Match percentages
  const percentages = combined.match(/(\d{1,3}(?:\.\d+)?%)/g);
  if (percentages) {
    percentages.forEach(p => {
      stats.push({ value: p, type: 'percentage' });
    });
  }

  // Match money values
  const money = combined.match(/(\$\d{1,3}(?:,\d{3})*(?:\.\d+)?[MBKmk]?)/g);
  if (money) {
    money.forEach(m => {
      stats.push({ value: m, type: 'money' });
    });
  }

  // Match multipliers
  const multipliers = combined.match(/(\d+(?:\.\d+)?[xX])/g);
  if (multipliers) {
    multipliers.forEach(m => {
      stats.push({ value: m, type: 'multiplier' });
    });
  }

  return stats.slice(0, 3); // Max 3 stats
}

/**
 * Parse timeline items from bullets
 *
 * @param {Array} bullets - Bullet point strings
 * @returns {Array} Array of { year, text } objects
 */
export function parseTimelineItems(bullets) {
  // Match various date/era formats:
  // - Modern years: 1921, 2024
  // - BC/BCE/AD/CE dates: 9000 BC, 500 BCE
  // - Approximate: c. 9000, c. 500 BC
  // - Centuries: 7th Century, 16th century
  // - Era labels: Post WWI, Pre-1900
  const datePatterns = [
    /\b(19|20)\d{2}\b/,                              // Modern years
    /c\.\s*\d+\s*(?:BC|BCE|AD|CE)?\b/i,              // Approximate dates
    /\b\d+\s*(?:BC|BCE|AD|CE)\b/i,                   // BC/BCE/AD/CE dates
    /\b\d+(?:st|nd|rd|th)\s+century\b/i,             // Century patterns
    /\b(?:pre|post|early|late|mid)[- ]\w+/i,          // Era prefixes
  ];

  return bullets.map(bullet => {
    let year = '';
    let text = bullet;

    // Try each date pattern
    for (const pattern of datePatterns) {
      const match = bullet.match(pattern);
      if (match) {
        year = match[0];
        text = bullet.replace(match[0], '').replace(/^\s*[-–—:]\s*/, '').trim();
        break;
      }
    }

    // Fallback: if no date pattern matched, try splitting on first colon/dash
    if (!year && /[:–—]/.test(bullet)) {
      const parts = bullet.split(/\s*[:–—]\s*/);
      if (parts.length >= 2 && parts[0].length <= 30) {
        year = parts[0];
        text = parts.slice(1).join(': ').trim();
      }
    }

    return { year, text };
  }).filter(item => item.year || item.text);
}

/**
 * Split bullets for two-column layout
 *
 * @param {Array} bullets - Bullet point strings
 * @returns {Object} { left: [], right: [] }
 */
export function splitBulletsForColumns(bullets) {
  const midpoint = Math.ceil(bullets.length / 2);
  return {
    left: bullets.slice(0, midpoint),
    right: bullets.slice(midpoint),
  };
}

export default {
  detectLayoutPattern,
  getPatternMetadata,
  analyzeSlidePatterns,
  extractStatistics,
  parseTimelineItems,
  splitBulletsForColumns,
};
